import { USER_TYPE } from './constants/user';
import { STORAGE_KEYS } from './constants/storageKeys';
import { PARTNER_REDIRECT_HANDOFF, PARTNER_REDIRECT_FAILED_PARAM, RECUR_PRODUCT_NAME } from './constants/commonConstants';

function readAuthTuple(store: Storage): { token: string | null; orgId: string | null; userId: string | null } {
    return {
        token: store.getItem(STORAGE_KEYS.AUTH_TOKEN),
        // The partner's OWN org lives under user_org_id — org_id holds the investor/PNO org the
        // partner is scoped to, which partner-platform's auth-match would reject (RECUR-27790).
        orgId: store.getItem(STORAGE_KEYS.USER_ORG_ID),
        userId: store.getItem(STORAGE_KEYS.USER_ID),
    };
}

/**
 * No fallback by design: the redirect must fail closed when this env var is unset (e.g. on
 * dev/staging, where it's deliberately left out), rather than defaulting to a hardcoded prod URL.
 */
export function getPartnerPlatformUrl(): string | undefined {
    return process.env.REACT_APP_PARTNER_PLATFORM_URL || undefined;
}

/**
 * Reads the currently authenticated session's token/orgId/userId ATOMICALLY from a single
 * storage backend — never mixes a fresh sessionStorage value with a stale localStorage one,
 * which could otherwise hand off under the wrong partner context. Falls back to localStorage
 * only when sessionStorage holds none of the three values.
 */
export function readAuthForHandoff(): { token: string | null; orgId: string | null; userId: string | null } {
    const sessionTuple = readAuthTuple(sessionStorage);
    if (sessionTuple.token || sessionTuple.orgId || sessionTuple.userId) {
        return sessionTuple;
    }
    return readAuthTuple(localStorage);
}

/**
 * Non-secret ids go in the query string. The token and email go in the URL fragment so they
 * never reach server logs — partner-platform's /login bootstrap needs the email (its config +
 * T&C calls require it) but the Cerberus token carries no email claim, so AICA forwards the one
 * it already collected at login (RECUR-27790).
 */
export function buildPartnerHandoffUrl(
    baseUrl: string,
    token: string | null,
    orgId: string | null,
    userId: string | null,
    email: string | null,
): string {
    const params = new URLSearchParams({
        [PARTNER_REDIRECT_HANDOFF.ORG_ID]: orgId ?? '',
        [PARTNER_REDIRECT_HANDOFF.USER_ID]: userId ?? '',
        [PARTNER_REDIRECT_HANDOFF.RETURN_URL]: window.location.origin,
    });
    const fragment = new URLSearchParams({
        [PARTNER_REDIRECT_HANDOFF.TOKEN]: token ?? '',
        [PARTNER_REDIRECT_HANDOFF.EMAIL]: email ?? '',
    });
    return `${baseUrl}?${params.toString()}#${fragment.toString()}`;
}

export function shouldRedirectPartner({
    accountType,
    isConfigLoaded,
    isRedirectEnabled,
    productName,
    token,
    orgId,
    userId,
    url,
    email,
}: {
    accountType?: string;
    isConfigLoaded: boolean;
    isRedirectEnabled: boolean;
    productName?: string;
    token?: string | null;
    orgId?: string | null;
    userId?: string | null;
    url?: string;
    email?: string | null;
}): boolean {
    // productName === RECUR_PRODUCT_NAME is a Recur-scope guard, not redundant with
    // isRedirectEnabled: partner-platform is hardwired to the Recur org/platform, so a token
    // minted under a non-Recur PNO would be rejected on the other side regardless of the flag.
    return (
        accountType === USER_TYPE.PARTNER &&
        isConfigLoaded === true &&
        isRedirectEnabled === true &&
        productName === RECUR_PRODUCT_NAME &&
        !!token &&
        !!orgId &&
        !!userId &&
        !!url &&
        !!email
    );
}

/**
 * True when the redirect must not fire: either partner-platform already bounced this session
 * back (avoids an infinite redirect loop), or this tab is a PNO admin's summon/impersonation
 * session. A summon tab writes auth_token to sessionStorage (summonProcess only) — a normal
 * login stores the token in localStorage instead — so a non-null sessionStorage auth_token
 * reliably marks impersonation and must never be redirected cross-origin with the summoned
 * user's token.
 */
export function isPartnerRedirectSuppressed(): boolean {
    if (sessionStorage.getItem(STORAGE_KEYS.AUTH_TOKEN) !== null) return true;
    return new URLSearchParams(window.location.search).get(PARTNER_REDIRECT_FAILED_PARAM) === '1';
}
